/**
 * marginEngine.js — Pure-function margin calculation engine
 *
 * Implements rules M-1 through M-7 from DATA_AND_STATUS_CONTRACT §1.
 * All functions are pure (no side effects, no state mutation).
 * All monetary values are in cents (integers) to avoid floating-point errors.
 * Margin percentages are expressed as decimals (0.35 = 35%).
 *
 * Rules:
 *   M-1: Line-item margin defaults from product category
 *   M-2: Overall/default margin applies ONLY to lines with no line-item margin
 *   M-3: Calculated gross margin = blended realized margin (read-only)
 *   M-4: Two-way bind: margin% ↔ price recomputes (cost stays fixed)
 *   M-5: Bid Total is ALWAYS read-only (Σ line totals)
 *   M-6: Cost is read-only for catalog items; editable for custom items
 *   M-7: Realized gross margin is what Reports consume
 */

// ─── Core Calculations ───────────────────────────────────────────────────────

/**
 * M-4: Calculate line total from cost and margin percentage.
 * Formula: line_total = cost / (1 - margin_pct)
 *
 * @param {number} cost - Line cost in cents (material + labor)
 * @param {number} marginPct - Margin as decimal (0.35 = 35%)
 * @returns {number} Line total in cents (rounded to nearest cent)
 */
export function lineTotal(cost, marginPct) {
  if (cost < 0) throw new RangeError('Cost cannot be negative');
  if (marginPct >= 1) throw new RangeError('Margin cannot be 100% or greater (division by zero)');
  if (marginPct < 0) throw new RangeError('Margin cannot be negative');
  if (cost === 0) return 0;
  return Math.round(cost / (1 - marginPct));
}

/**
 * IDI-420: the largest margin `lineTotal` can evaluate.
 *
 * `lineTotal` divides by `(1 - marginPct)`, so a margin of exactly 1 (100%) is
 * a division by zero and anything above it is negative. The throw above is
 * correct arithmetic, but it is a HARD failure: it propagates out of
 * computeQuote during render and white-screens the whole quote builder.
 *
 * Callers that accept user input must clamp BEFORE storing, so the invalid
 * value never reaches a render. 0.99 = 99%, the highest whole percent that
 * still divides.
 */
export const MAX_MARGIN_PCT = 0.99;

/**
 * IDI-420: clamp a margin decimal into the range `lineTotal` can evaluate.
 * Non-finite input collapses to 0 rather than NaN (NaN >= 1 is false, so a NaN
 * would slip past the guard and make every downstream total NaN).
 *
 * @param {number} marginPct - Margin as a decimal (0.35 = 35%)
 * @returns {number} A value in [0, MAX_MARGIN_PCT]
 */
export function clampMarginPct(marginPct) {
  const n = Number(marginPct);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > MAX_MARGIN_PCT) return MAX_MARGIN_PCT;
  return n;
}

/**
 * M-4: Back-calculate margin percentage from cost and line total (price).
 * Formula: margin_pct = (line_total - cost) / line_total
 *
 * @param {number} cost - Line cost in cents
 * @param {number} price - Line total/price in cents
 * @returns {number} Margin as decimal (0.50 = 50%)
 */
export function marginFromPrice(cost, price) {
  if (price <= 0) return 0;
  if (cost < 0) throw new RangeError('Cost cannot be negative');
  if (cost > price) return 0; // Negative margin clamped to 0
  return (price - cost) / price;
}

/**
 * M-4: Calculate price from cost and margin percentage.
 * Alias for lineTotal — same formula, different semantic name.
 *
 * @param {number} cost - Line cost in cents
 * @param {number} marginPct - Margin as decimal (0.35 = 35%)
 * @returns {number} Price in cents
 */
export function priceFromMargin(cost, marginPct) {
  return lineTotal(cost, marginPct);
}

/**
 * M-3 / M-7: Calculate blended (realized) gross margin across all lines.
 * Formula: blended = Σ(line_total - line_cost) / Σ(line_total)
 *
 * This is the "true" margin after all per-line overrides.
 * Reports F1 consumes this value (M-7).
 *
 * @param {Array<{cost: number, total: number}>} lines - Array of line items
 * @returns {number} Blended margin as decimal (0.296 = 29.6%)
 */
export function blendedMargin(lines) {
  if (!lines || lines.length === 0) return 0;

  let totalRevenue = 0;
  let totalCost = 0;

  for (const line of lines) {
    totalRevenue += line.total;
    totalCost += line.cost;
  }

  if (totalRevenue <= 0) return 0;
  return (totalRevenue - totalCost) / totalRevenue;
}

/**
 * M-5: Calculate bid total (read-only sum of all line totals).
 * Bid Total is NEVER directly editable.
 *
 * @param {Array<{total: number}>} lines - Array of line items
 * @returns {number} Bid total in cents
 */
export function bidTotal(lines) {
  if (!lines || lines.length === 0) return 0;
  return lines.reduce((sum, line) => sum + line.total, 0);
}

// ─── Cost Breakdown ──────────────────────────────────────────────────────────

/**
 * Calculate cost breakdown by category (Material / Labor / Other).
 *
 * @param {Array<{materialCost: number, laborCost: number, otherCost?: number, total: number}>} lines
 * @param {number} [laborBurdenPct=0] - IDI-205: account-wide labor burden, as
 *   a 0-100 PERCENT (matching the Settings → Quote Defaults field, NOT a
 *   0-1 decimal like margin). Applied ONCE to the TOTAL labor bucket after
 *   summing every line (per stakeholder ruling: "whatever your total labor
 *   bucket is, multiply that by 10%" — not per-line, not to material/other).
 *   Formula: labor_burden_amount = total_labor_bucket × (pct / 100).
 *   Cost-side only: increases the Labor bucket / Total Cost (and therefore
 *   the derived margin/percentage figures), but does NOT change bidTotal
 *   (Σ line totals) — M-5 elsewhere in this file makes Bid Total
 *   unconditionally read-only/never-directly-editable, and burden has no
 *   per-line home to attach a price increase to.
 * @returns {{material: number, labor: number, other: number, totalCost: number, totalRevenue: number, materialPct: number, laborPct: number, otherPct: number, laborBurdenAmount: number}}
 */
export function costBreakdown(lines, laborBurdenPct = 0) {
  if (!lines || lines.length === 0) {
    return {
      material: 0,
      labor: 0,
      other: 0,
      totalCost: 0,
      totalRevenue: 0,
      materialPct: 0,
      laborPct: 0,
      otherPct: 0,
      laborBurdenAmount: 0,
    };
  }

  let material = 0;
  let labor = 0;
  let other = 0;
  let totalRevenue = 0;

  for (const line of lines) {
    material += line.materialCost || 0;
    labor += line.laborCost || 0;
    other += line.otherCost || 0;
    // IDI-177: additionalLabor and additionalCost are dollar amounts (not
    // cents) stored on the line, entered against DIFFERENT cost categories on
    // the line-item form -- additionalLabor is extra LABOR, additionalCost is
    // extra OTHER cost. Both were previously dumped into "Other", which made
    // the Labor bucket silently understate real labor cost by however much
    // additional labor was entered on any line, and inflated "Other" to
    // compensate. Split them into their own matching buckets instead.
    labor += Math.round((Number(line.additionalLabor ?? line.additional_labor ?? 0) || 0) * 100);
    other += Math.round((Number(line.additionalCost ?? line.additional_cost ?? 0) || 0) * 100);
    totalRevenue += line.total || 0;
  }

  // IDI-205: apply labor burden to the FULLY-SUMMED labor bucket (including
  // the IDI-177 additionalLabor fix above) -- a fix that landed one without
  // the other would still ship a wrong Labor figure, per the ticket's own
  // cross-reference.
  const pct = Number(laborBurdenPct) || 0;
  const laborBurdenAmount = pct > 0 ? Math.round(labor * (pct / 100)) : 0;
  labor += laborBurdenAmount;

  const totalCost = material + labor + other;

  return {
    material,
    labor,
    other,
    totalCost,
    totalRevenue,
    materialPct: totalCost > 0 ? material / totalCost : 0,
    laborPct: totalCost > 0 ? labor / totalCost : 0,
    otherPct: totalCost > 0 ? other / totalCost : 0,
    laborBurdenAmount,
  };
}

// ─── Margin Resolution (M-1, M-2 + PT-30 Lindsey precedence) ───────────────

/**
 * Predicate: is a margin value explicitly set?
 *
 * Extracted per PT-30 §30.2 so every level of the precedence chain uses the
 * exact same rule. `0` is a legitimate margin and MUST short-circuit further
 * fallback (this is the failure that produced the legacy 5% workaround —
 * PT-30 explicitly does not inherit it). `null` and `undefined` cascade.
 * Negative values (including sentinel `-1`) are NOT considered set.
 *
 * @param {number|null|undefined} value
 * @returns {boolean}
 */
function isMarginSet(value) {
  return value != null && Number.isFinite(value) && value >= 0;
}

/**
 * PT-30 · Lindsey's precedence (2026-07-24 walkthrough) + preserved per-line override.
 *
 * Five-level cascade — first level that is set wins. Zero and null are valid
 * inputs at every level; 0 counts as "explicitly set" and short-circuits.
 *
 *   1. lineMargin          — per-line manual override (preserved from prior engine;
 *                            not in Lindsey's diagram but the only way a user
 *                            overrides one line without moving any default)
 *   2. categoryMargin      — Item Category Margin (Lindsey level 1)
 *   3. customerMargin      — Customer Specific Margin (Lindsey level 2)
 *   4. customerTypeMargin  — Customer Type Margin (Lindsey level 3)
 *   5. quoteMargin         — User-Entered Quote Margin (Lindsey level 4;
 *                            legacy alias: `overallMargin`)
 *
 * Backward compatibility: if `quoteMargin` is undefined but `overallMargin` is
 * provided (old callers), `overallMargin` is used as the level-5 value. New
 * callers should pass `quoteMargin` and stop passing `overallMargin`.
 *
 * Spec of record: `docs/pt-30/PRECEDENCE.md`.
 *
 * @param {object} params
 * @param {number|null} [params.lineMargin]         Level 1 — per-line manual override
 * @param {number|null} [params.categoryMargin]     Level 2 — item-category default
 * @param {number|null} [params.customerMargin]     Level 3 — customer-specific default
 * @param {number|null} [params.customerTypeMargin] Level 4 — customer-type default
 * @param {number|null} [params.quoteMargin]        Level 5 — user-entered quote margin
 * @param {number|null} [params.overallMargin]      DEPRECATED alias for `quoteMargin`
 * @returns {number} Effective margin as decimal (0..0.999…); 0 if none set.
 */
export function resolveMargin({
  lineMargin,
  categoryMargin,
  customerMargin,
  customerTypeMargin,
  quoteMargin,
  overallMargin,
} = {}) {
  if (isMarginSet(lineMargin)) return lineMargin;
  if (isMarginSet(categoryMargin)) return categoryMargin;
  if (isMarginSet(customerMargin)) return customerMargin;
  if (isMarginSet(customerTypeMargin)) return customerTypeMargin;

  // Level 5: prefer new `quoteMargin`, fall back to legacy `overallMargin`.
  const level5 = quoteMargin != null ? quoteMargin : overallMargin;
  if (isMarginSet(level5)) return level5;

  // Nothing set at any level. Explicit zero is the safe fallback — no 5% workaround.
  return 0;
}

// ─── Line Item Computation ───────────────────────────────────────────────────

/**
 * Normalize the second argument of `computeLineItem` into a full margin
 * context object. Backward compat: existing callers pass a bare number
 * (the legacy `overallMargin`); new callers pass an object with
 * `{ customerMargin, customerTypeMargin, quoteMargin }` (see PT-30 §30.2).
 *
 * A number is treated as `quoteMargin` (Lindsey level 4).
 */
function normalizeMarginContext(arg) {
  if (arg == null) return {};
  if (typeof arg === 'number') return { quoteMargin: arg };
  if (typeof arg === 'object') {
    // Accept both `quoteMargin` (new) and `overallMargin` (legacy) as level 5.
    const {
      customerMargin,
      customerTypeMargin,
      quoteMargin,
      overallMargin,
      preferStamp,
    } = arg;
    return {
      customerMargin,
      customerTypeMargin,
      quoteMargin: quoteMargin != null ? quoteMargin : overallMargin,
      preferStamp: preferStamp === true,
    };
  }
  return {};
}

/**
 * Compute a fully-resolved line item with all derived fields.
 * Combines M-1 through M-6 with the PT-30 Lindsey precedence.
 *
 * @param {object} item - Raw line item data
 * @param {number} item.materialCost - Material cost in cents
 * @param {number} item.laborCost - Labor cost in cents
 * @param {number} item.otherCost - Other cost in cents (optional, default 0)
 * @param {number|null} item.lineMargin - User override margin (decimal, null if not set)
 * @param {number|null} item.lineTotal - User override price (cents, null if not set)
 * @param {number|null} item.categoryMargin - Product category default margin (decimal)
 * @param {boolean} item.isCustom - Whether this is a custom item (M-6: editable cost)
 * @param {number|object} [marginContext] - Either a bare number (legacy — treated as
 *   `quoteMargin`) or a full margin context object with any of
 *   `{ customerMargin, customerTypeMargin, quoteMargin, overallMargin }`.
 * @returns {object} Computed line item with all derived fields
 */
export function computeLineItem(item, marginContext = 0) {
  const ctx = normalizeMarginContext(marginContext);
  // Tolerance fix (2026-08-07): accept snake_case cost fields as fallback.
  // Some wire records carry `material_cost`/`labor_cost`/`other_cost` instead
  // of the camelCase forms. Using `??` so an explicit 0 still counts as set.
  const materialCost = Number(item.materialCost ?? item.material_cost ?? 0) || 0;
  const laborCost = Number(item.laborCost ?? item.labor_cost ?? 0) || 0;
  const otherCost = Number(item.otherCost ?? item.other_cost ?? 0) || 0;
  // B0 (Iter-20): additionalLabor and additionalCost are user-entered DOLLAR
  // amounts (the input label says "$"). Convert to cents before adding to cost
  // so they carry margin like every other cost component.
  //
  // §2 GATE (Iter-20 pre-merge): when preferStamp is true, the record was sent
  // BEFORE B0 existed — its stored lineTotal was derived from cost WITHOUT these
  // fields. Including them now would widen `cost`, make `computedTotal` diverge
  // from the stored lineTotal, and falsely trigger `isRealPriceOverride`. The
  // stamp path must reproduce the derivation that produced the stored number.
  // On DRAFT (preferStamp=false) the full cost is used so new quotes price
  // correctly going forward.
  const additionalLaborCents = Math.round(
    (Number(item.additionalLabor ?? item.additional_labor ?? 0) || 0) * 100
  );
  const additionalCostCents = Math.round(
    (Number(item.additionalCost ?? item.additional_cost ?? 0) || 0) * 100
  );

  // §2 (RS-2, 2026-08-07): READ STAMP ONLY WHEN EXPLICITLY REQUESTED.
  //
  // Per DOCUMENT_SURFACE_SPEC RS-2: "resolve live while DRAFT, STAMP on send".
  //   - DRAFT reads (default): ignore stamps; recompute so drafts respond to
  //     settings changes (a corrected category margin picks up immediately).
  //   - SENT reads (preferStamp=true): honour stamps; the record must match
  //     the PDF the customer received, forever.
  //
  // The caller signals which mode via `ctx.preferStamp`. `useQuoteMargins`
  // wires this from the quote's `isSent` state.
  const preferStamp = ctx.preferStamp === true;

  // Gate: sent records exclude additionalLabor/Cost from cost derivation.
  // These fields were inert when the record was sent; including them now
  // would produce a different total and trigger false price-override detection.
  const cost = preferStamp
    ? materialCost + laborCost + otherCost
    : materialCost + laborCost + otherCost + additionalLaborCents + additionalCostCents;
  const stampedMargin = preferStamp ? (item.resolved_margin ?? item.resolvedMargin) : null;
  const stampedSource = preferStamp ? (item.margin_source ?? item.marginSource) : null;
  const hasStamp =
    stampedMargin != null && Number.isFinite(Number(stampedMargin)) &&
    typeof stampedSource === 'string' && stampedSource !== '';

  // PT-30: Resolve margin via the 5-level Lindsey precedence.
  // Tolerance fix (2026-08-07): accept both camelCase and snake_case for
  // categoryMargin/lineMargin so wire records with either shape resolve
  // correctly.
  const lineMarginVal = item.lineMargin ?? item.line_margin;
  const categoryMarginVal = item.categoryMargin ?? item.category_margin;
  // Fresh resolution (used both as the fallback for unstamped lines AND
  // as the reference for stamp-plus-price-override detection).
  const freshMargin = resolveMargin({
    lineMargin: lineMarginVal,
    categoryMargin: categoryMarginVal,
    customerMargin: ctx.customerMargin,
    customerTypeMargin: ctx.customerTypeMargin,
    quoteMargin: ctx.quoteMargin,
  });

  let freshSource = 'quote_default';
  if (isMarginSet(lineMarginVal)) freshSource = 'line_override';
  else if (isMarginSet(categoryMarginVal)) freshSource = 'category_default';
  else if (isMarginSet(ctx.customerMargin)) freshSource = 'customer_override';
  else if (isMarginSet(ctx.customerTypeMargin)) freshSource = 'customer_type_default';

  // The AUTHORITATIVE tier for THIS line: stamp wins, fresh is fallback.
  const resolvedMargin = hasStamp ? Number(stampedMargin) : freshMargin;
  const marginSource = hasStamp ? stampedSource : freshSource;

  // M-4: recompute the "would-be" price from cost + resolved margin.
  const computedTotal = lineTotal(cost, resolvedMargin);

  // Price-override detection (fix, 2026-08-07 / §2):
  //   Presence of a stored `lineTotal` is NOT evidence of a manual override
  //   — it's the persisted COMPUTED price, present on every saved line.
  //   The distinguishing test: does the stored price match cost/(1-margin)?
  //   - Matches (within 1 cent for rounding): the stored value is computed
  //     from the resolved margin tier → attribute to that tier.
  //   - Diverges: someone manually entered a price → attribute as override.
  //
  //   Intent-vs-presence pattern (see §3 note in R20-4 rollout): a stored
  //   value that could either be a computed default OR a deliberate choice
  //   MUST NOT be treated as the latter without evidence of intent.
  const wireLineTotal = item.lineTotal ?? item.line_total;

  // §2a RECONCILE-BOTH (Iter-20): when preferStamp is true, the stored lineTotal
  // could have been derived from EITHER cost formula:
  //   - pre-B0:  cost = material + labor + other  (additionalLabor was inert)
  //   - post-B0: cost = material + labor + other + additionalLabor + additionalCost
  // Rather than guessing which era produced the stored number, check both.
  // A match against either means the stored total is engine-derived, not a manual
  // override. This handles both populations without a migration or stamp change.
  const costWithExtras = materialCost + laborCost + otherCost + additionalLaborCents + additionalCostCents;
  const computedTotalWithExtras = lineTotal(costWithExtras, resolvedMargin);

  const isRealPriceOverride =
    wireLineTotal != null &&
    wireLineTotal > 0 &&
    Math.abs(wireLineTotal - computedTotal) > 1 &&
    Math.abs(wireLineTotal - computedTotalWithExtras) > 1; // matches neither candidate

  if (isRealPriceOverride) {
    // Determine which cost basis is closer to what produced the stored total.
    const effectiveCost = (Math.abs(wireLineTotal - computedTotalWithExtras) <
      Math.abs(wireLineTotal - computedTotal)) ? costWithExtras : cost;
    const overrideMargin = marginFromPrice(effectiveCost, wireLineTotal);
    return {
      materialCost,
      laborCost,
      otherCost,
      // IDI-177: costBreakdown() reads line.additionalLabor/additionalCost
      // directly off each computed line to split them into their own
      // Labor/Other buckets (rather than dumping both into "Other", the
      // original bug). computeLineItem's return object never carried these
      // fields through, so costBreakdown always saw undefined -> 0,
      // regardless of what was actually entered on the line -- even though
      // `cost`/`total` above DID correctly fold them in. Pass through in
      // DOLLARS (matching costBreakdown's own `* 100` conversion and the
      // original item's unit), and respect the same preferStamp gate the
      // cost derivation above uses (sent records exclude them).
      // IDI-493: report the extras exactly when the COST used includes them.
      // The old `preferStamp ? 0` dropped them on every sent record, even one
      // whose stored price was derived WITH them (all post-B0 quotes), so a
      // sent/signed quote's Cost Breakdown lost Labor + Other while its total
      // didn't move.
      additionalLabor: effectiveCost === costWithExtras ? additionalLaborCents / 100 : 0,
      additionalCost: effectiveCost === costWithExtras ? additionalCostCents / 100 : 0,
      cost: effectiveCost,
      margin: overrideMargin,
      total: wireLineTotal,
      isCustom: !!item.isCustom,
      marginSource: 'price_override',
    };
  }

  // No manual override: use the resolved margin tier.
  // Return the computed total, or the stored one when they agree (they will).
  // For the cost field: use whichever formula matches the stored total.
  const total = wireLineTotal != null && wireLineTotal > 0 ? wireLineTotal : computedTotal;
  const returnCost = (wireLineTotal != null && wireLineTotal > 0 &&
    Math.abs(wireLineTotal - computedTotalWithExtras) <= 1) ? costWithExtras : cost;

  return {
    materialCost,
    laborCost,
    otherCost,
    // IDI-177: see comment on the price_override branch above.
    // IDI-493: same rule as above. The breakdown buckets follow whichever
    // cost formula matches the stored price (returnCost), so they always add
    // up to the line's cost: post-B0 sent quotes keep their extras, pre-B0
    // ones (stored price derived without them) still exclude them.
    additionalLabor: returnCost === costWithExtras ? additionalLaborCents / 100 : 0,
    additionalCost: returnCost === costWithExtras ? additionalCostCents / 100 : 0,
    cost: returnCost,
    margin: resolvedMargin,
    total,
    isCustom: !!item.isCustom,
    marginSource,
  };
}

// ─── Quote-Level Computation ─────────────────────────────────────────────────

/**
 * PR-12 (2026-07-22): Sum the effective discount for a subtotal, given the
 * builder's `discounts[]` array. Discounts are stored in DOLLARS but math
 * happens in CENTS. Operator ruling: discount is computed on the pre-tax
 * subtotal and applied BEFORE tax.
 *
 * Schema per DiscountSection.js:
 *   { type: 'percent' | 'fixed', value: number, description?: string }
 *   - percent: value is 0-100 (e.g. 10 → 10%)
 *   - fixed:   value is in DOLLARS (e.g. 500 → $500.00)
 *
 * Multiple discounts sum. Total is clamped at subtotal (can't go negative).
 * Matches QuoteReviewSend.sumDiscounts semantics so builder rail + customer
 * document produce identical totals.
 *
 * @param {Array<{type: string, value: number}>} discounts
 * @param {number} subtotalCents - Pre-discount subtotal in cents
 * @returns {number} Total discount in cents (>= 0, <= subtotalCents)
 */
export function sumDiscountsCents(discounts, subtotalCents) {
  if (!Array.isArray(discounts) || discounts.length === 0) return 0;
  if (subtotalCents <= 0) return 0;

  let totalCents = 0;
  for (const d of discounts) {
    const value = Number(d?.value ?? 0);
    if (!Number.isFinite(value) || value <= 0) continue;
    if (d?.type === 'percent') {
      totalCents += Math.round((subtotalCents * value) / 100);
    } else {
      // 'fixed' (or unknown → treat as fixed dollars, matches QuoteReviewSend legacy)
      totalCents += Math.round(value * 100);
    }
  }

  // Clamp: discount can't exceed subtotal.
  return Math.max(0, Math.min(totalCents, subtotalCents));
}

/**
 * Compute all derived values for an entire quote.
 * Combines all M-1 through M-7 rules into a single quote summary.
 *
 * PR-12 (2026-07-22): Now accepts `discounts` and applies them BEFORE tax
 * (operator ruling). Prior behavior taxed the pre-discount subtotal, which
 * meant the discount never actually reduced the Bid Total shown in the
 * Estimate rail. Now:
 *
 *   subtotal         = Σ line totals              (unchanged — M-5)
 *   discountAmount   = sumDiscountsCents(...)     (NEW)
 *   taxableSubtotal  = subtotal - discountAmount  (NEW)
 *   taxAmount        = round(taxableSubtotal * taxRate)
 *   bidTotal         = taxableSubtotal + taxAmount
 *
 * @param {Array<object>} items - Raw line items
 * @param {number|object} [marginContext=0] - Either a bare number (legacy —
 *   treated as `quoteMargin`) or a full PT-30 margin context object with any of
 *   `{ customerMargin, customerTypeMargin, quoteMargin, overallMargin }`.
 * @param {number} [taxRate=0] - Tax rate as decimal (0.07 = 7%)
 * @param {Array<{type: string, value: number}>} [discounts=[]] - Builder discounts
 * @param {number} [laborBurdenPct=0] - IDI-205: account-wide labor burden as a
 *   0-100 percent, threaded straight through to costBreakdown. See that
 *   function's docstring for the exact formula and why it's cost-side only.
 * @returns {object} Full quote computation
 */
export function computeQuote(items, marginContext = 0, taxRate = 0, discounts = [], laborBurdenPct = 0) {
  if (!items || items.length === 0) {
    return {
      lines: [],
      costBreakdown: costBreakdown([], laborBurdenPct),
      blendedMargin: 0,
      bidSubtotal: 0,
      discountAmount: 0,
      taxableSubtotal: 0,
      taxAmount: 0,
      bidTotal: 0,
      marginDollars: 0,
    };
  }

  // Compute each line (PT-30: marginContext may be a number OR the full 5-level context object).
  const computedLines = items.map((item) => computeLineItem(item, marginContext));

  // M-3 / M-7: Blended realized margin
  const realizedMargin = blendedMargin(computedLines);

  // M-5: Bid subtotal (pre-discount, pre-tax) — read-only sum of line totals.
  const subtotal = bidTotal(computedLines);

  // PR-12: Apply builder discounts BEFORE tax (operator ruling).
  const discountAmount = sumDiscountsCents(discounts, subtotal);
  const taxableSubtotal = subtotal - discountAmount;

  // Tax on the DISCOUNTED subtotal.
  const taxAmount = Math.round(taxableSubtotal * taxRate);

  // Final Bid Total = discounted subtotal + tax.
  const total = taxableSubtotal + taxAmount;

  // Cost breakdown (unchanged — discount does not affect cost).
  const breakdown = costBreakdown(computedLines, laborBurdenPct);

  // Margin dollars (revenue - cost) — realized against pre-discount revenue.
  // Note: discount reduces realized margin, but per M-7 the reported margin
  // is based on line-item math; discount is a customer-facing concession.
  const marginDollars = subtotal - breakdown.totalCost;

  return {
    lines: computedLines,
    costBreakdown: breakdown,
    // IDI-848 D2: with a burden, Gross Margin % counts it (agrees with
    // marginDollars). With none, today's realized margin, byte-for-byte.
    blendedMargin: breakdown.laborBurdenAmount > 0 && subtotal !== 0
      ? (subtotal - breakdown.totalCost) / subtotal
      : realizedMargin,
    bidSubtotal: subtotal,
    discountAmount,
    taxableSubtotal,
    taxAmount,
    bidTotal: total,
    marginDollars,
  };
}

// ─── Utility Helpers ─────────────────────────────────────────────────────────

/**
 * Format margin as percentage string for display.
 * @param {number} margin - Margin as decimal (0.35)
 * @returns {string} Formatted string ("35.0%")
 */
export function formatMarginPct(margin) {
  return `${(margin * 100).toFixed(1)}%`;
}

/**
 * Parse a percentage string back to decimal.
 * @param {string} str - Percentage string ("35", "35%", "35.5%")
 * @returns {number} Decimal (0.35, 0.355)
 */
export function parseMarginPct(str) {
  const cleaned = String(str).replace('%', '').trim();
  const num = parseFloat(cleaned);
  if (isNaN(num)) return 0;
  return num / 100;
}

/**
 * IDI-420: field-level validation for any user-typed "Margin %" (percent
 * units, e.g. "35"). ONE rule for every margin input, so no field can be left
 * unguarded (the custom-item field was, after #479 fixed the Options one).
 *
 * lineTotal divides by (1 - margin), so a margin of 100% or more is not
 * representable and throws a RangeError during a later render, which crashes
 * the whole builder route. Refuse it at the field instead.
 *
 * @param {string|number|null|undefined} raw
 * @returns {string|null} a user-facing message, or null when valid or empty
 */
export function marginPctError(raw) {
  if (raw === '' || raw == null) return null;
  const cleaned = String(raw).replace('%', '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return 'Enter a number';
  if (n < 0) return 'Margin cannot be negative';
  if (n >= 100) return `Margin must be less than 100% (max ${MAX_MARGIN_PCT * 100}%)`;
  return null;
}

/**
 * Parse a percentage input that MAY be unset. Distinct from `parseMarginPct`:
 * unset (null, undefined, '') and malformed (NaN, non-numeric) collapse to
 * `null`, and an explicit `0` is preserved as `0` (a legitimate margin per
 * §3q; see marginEngine.isMarginSet at line 209).
 *
 * Introduced by 372_ §2 to replace two truthy-ternary call sites that would
 * silently destroy an explicit zero under any refactor of caller state from
 * string to Number. Callers:
 *   • AddItemCascade.js  —  custom-item form Margin % field
 *   • CustomItemModal.js —  quote-detail Add-Custom form Margin % field
 *
 * Semantics table:
 *   parseNullableMarginPct('0')       -> 0        (STRING zero, preserved)
 *   parseNullableMarginPct(0)         -> 0        (NUMBER zero, preserved)
 *   parseNullableMarginPct('35')      -> 0.35
 *   parseNullableMarginPct('35%')     -> 0.35     (% suffix stripped)
 *   parseNullableMarginPct(35)        -> 0.35
 *   parseNullableMarginPct('')        -> null     (unset)
 *   parseNullableMarginPct(null)      -> null
 *   parseNullableMarginPct(undefined) -> null
 *   parseNullableMarginPct('abc')     -> null     (malformed, not 0)
 *   parseNullableMarginPct(NaN)       -> null
 *
 * Contrast with `parseMarginPct` above, which returns `0` on NaN (safe for
 * display/formatter callers who never distinguish "unset" from "zero").
 *
 * @param {string|number|null|undefined} raw
 * @returns {number|null} decimal margin (e.g. 0.35 for 35%) or null when unset
 */
export function parseNullableMarginPct(raw) {
  if (raw === '' || raw == null) return null;
  const cleaned = String(raw).replace('%', '').trim();
  if (cleaned === '') return null;
  const num = parseFloat(cleaned);
  if (!Number.isFinite(num)) return null;
  return num / 100;
}

/**
 * Calculate the price difference for an alternate line item.
 * @param {number} originalTotal - Original line total in cents
 * @param {number} alternateTotal - Alternate line total in cents
 * @returns {number} Difference in cents (negative = cheaper alternate)
 */
export function alternatePriceDiff(originalTotal, alternateTotal) {
  return alternateTotal - originalTotal;
}
